import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '@shared/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../overlay/popover';
import { Input } from '../primitives/input';
import { Text } from '../typography/text';
import { LoadingStatus } from '../feedback/loading-status';
import {
  popoverPickerContentClass,
  popoverPickerListClass,
  popoverPickerSearchListClass,
  popoverPickerTriggerClass,
} from './popover-picker.styles';

export type PopoverSelectOption = {
  value: string;
  label: string;
  description?: string;
};

export type PopoverSelectProps = {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  value: string;
  options: Array<string | PopoverSelectOption>;
  onValueChange: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchValueChange?: (value: string) => void;
  showOptionsWithoutSearch?: boolean;
  /** Disable local matching when the owner searches the canonical collection. */
  filterOptions?: boolean;
  emptyText?: string;
  loading?: boolean;
  loadingLabel?: string;
  loadingMore?: boolean;
  loadingMoreLabel?: string;
  /** Whether the owner can request another incremental list page. */
  hasNextPage?: boolean;
  /** Changes only after the owner has successfully appended an incremental list page. */
  loadedPageCount?: number;
  /** Identifies the server-side selector scope independently from its loaded page count. */
  paginationScopeKey?: string;
  onListEndReached?: () => void;
  triggerAriaLabel?: string;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  /** Fit complete option labels within the available viewport; defaults to trigger width. */
  contentSizing?: 'trigger' | 'content';
  itemClassName?: string;
  'data-testid'?: string;
  searchInputTestId?: string;
};

export function PopoverSelect({
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  value,
  options,
  onValueChange,
  onOpenChange,
  disabled = false,
  required = false,
  placeholder,
  searchPlaceholder,
  searchValue,
  onSearchValueChange,
  showOptionsWithoutSearch = true,
  filterOptions = true,
  emptyText = 'No options match your search.',
  loading = false,
  loadingLabel = 'Loading',
  loadingMore = false,
  loadingMoreLabel = 'Loading more',
  hasNextPage = false,
  loadedPageCount,
  paginationScopeKey,
  onListEndReached,
  triggerAriaLabel,
  className,
  triggerClassName,
  contentClassName,
  contentSizing = 'trigger',
  itemClassName,
  'data-testid': dataTestId,
  searchInputTestId,
}: PopoverSelectProps) {
  const listboxId = useId();
  const autoLoadedPageCountRef = useRef<number | undefined>(undefined);
  const [listbox, setListbox] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [internalQuery, setInternalQuery] = useState('');
  const query = searchValue ?? internalQuery;
  const paginationIdentity = JSON.stringify([paginationScopeKey ?? '', query]);
  const previousPaginationIdentity = useRef(paginationIdentity);
  const setQuery = onSearchValueChange ?? setInternalQuery;
  const normalizedOptions = useMemo(() => {
    const optionMap = new Map<string, PopoverSelectOption>();

    for (const option of options) {
      const normalizedOption =
        typeof option === 'string' ? { value: option, label: option } : option;
      if (!optionMap.has(normalizedOption.value)) {
        optionMap.set(normalizedOption.value, normalizedOption);
      }
    }

    return Array.from(optionMap.values());
  }, [options]);
  const selectedOption = normalizedOptions.find((option) => option.value === value);
  const selectedLabel =
    selectedOption?.label ?? (loading && value ? loadingLabel : value || placeholder || '');
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (normalizedQuery.length === 0 && !showOptionsWithoutSearch) {
      return normalizedOptions.filter((option) => option.value === value);
    }

    if (!filterOptions || !searchPlaceholder || normalizedQuery.length === 0) {
      return normalizedOptions;
    }

    return normalizedOptions.filter((option) => {
      const haystack = `${option.label} ${option.description ?? ''} ${option.value}`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [filterOptions, normalizedOptions, query, searchPlaceholder, showOptionsWithoutSearch, value]);

  useLayoutEffect(() => {
    if (!open || previousPaginationIdentity.current !== paginationIdentity) {
      autoLoadedPageCountRef.current = undefined;
      previousPaginationIdentity.current = paginationIdentity;
      if (!open) return;
    }

    if (
      !hasNextPage ||
      loading ||
      loadingMore ||
      loadedPageCount === undefined ||
      autoLoadedPageCountRef.current === loadedPageCount ||
      !onListEndReached
    ) {
      return;
    }

    if (!listbox || listbox.scrollHeight - listbox.clientHeight > 32) return;

    autoLoadedPageCountRef.current = loadedPageCount;
    onListEndReached();
  }, [
    hasNextPage,
    listbox,
    loadedPageCount,
    loading,
    loadingMore,
    onListEndReached,
    open,
    paginationIdentity,
  ]);

  return (
    <Popover
      modal={false}
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        onOpenChange?.(nextOpen);
        if (!nextOpen) {
          setQuery('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          aria-required={required}
          aria-describedby={ariaDescribedBy}
          aria-label={triggerAriaLabel ?? selectedLabel}
          disabled={disabled}
          data-invalid={ariaInvalid}
          className={cn(
            popoverPickerTriggerClass,
            'aria-[invalid=true]:border-destructive',
            triggerClassName,
            className
          )}
          data-testid={dataTestId}
        >
          <span
            className={cn('line-clamp-1', !value ? 'text-muted-foreground' : 'text-foreground')}
          >
            {selectedLabel}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={0}
        avoidCollisions
        collisionPadding={16}
        className={cn(
          popoverPickerContentClass,
          contentSizing === 'content' &&
            'w-max min-w-[min(var(--radix-popover-trigger-width),var(--radix-popover-content-available-width))] max-w-[var(--radix-popover-content-available-width)]',
          contentClassName
        )}
      >
        {searchPlaceholder ? (
          <div className="p-1 pb-2">
            <Input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              aria-label={searchPlaceholder}
              placeholder={searchPlaceholder}
              className="h-10 px-3 py-2 text-sm"
              data-testid={searchInputTestId}
            />
          </div>
        ) : null}

        <div
          id={listboxId}
          ref={setListbox}
          role="listbox"
          aria-busy={loading || loadingMore || undefined}
          aria-label={triggerAriaLabel ?? searchPlaceholder ?? placeholder}
          className={searchPlaceholder ? popoverPickerSearchListClass : popoverPickerListClass}
          onScroll={(event) => {
            const list = event.currentTarget;
            if (list.scrollHeight - list.scrollTop - list.clientHeight <= 32) {
              onListEndReached?.();
            }
          }}
          onWheelCapture={(event) => {
            event.stopPropagation();
          }}
        >
          {loading ? (
            <LoadingStatus label={loadingLabel} />
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const isSelected = option.value === value;

              return (
                <button
                  key={option.value}
                  type="button"
                  className={cn(
                    'flex w-full items-center gap-2 rounded-none px-3 py-2 text-left text-base transition-colors hover:bg-accent hover:text-accent-foreground',
                    isSelected && 'bg-accent text-accent-foreground',
                    itemClassName
                  )}
                  onClick={() => {
                    onValueChange(option.value);
                    setOpen(false);
                  }}
                >
                  <span role="option" aria-selected={isSelected} className="min-w-0 flex-1">
                    <span
                      className={
                        contentSizing === 'content'
                          ? 'block whitespace-normal break-words'
                          : 'line-clamp-2 block'
                      }
                    >
                      {option.label}
                    </span>
                    {option.description ? (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {option.description}
                      </span>
                    ) : null}
                  </span>
                  {isSelected ? <Check className="h-4 w-4 shrink-0" /> : null}
                </button>
              );
            })
          ) : emptyText ? (
            <Text size="sm" color="muted" className="px-3 py-2">
              {emptyText}
            </Text>
          ) : null}
          {!loading && filteredOptions.length > 0 && loadingMore ? (
            <LoadingStatus label={loadingMoreLabel} />
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
